import React from 'react';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import { clearAppsDemoMode, setWelcomeModalOptions } from '../../actions/ui';
import { useHistory, useRouteMatch } from 'react-router-dom';
import routes from '../../routes';
import Title from 'antd/lib/typography/Title';
import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import { PlusOutlined } from '@ant-design/icons';
import { APP_SIGNUP_URL, MARKETING_SITE_DEMO_URL, USERFLOW_DEMO_FLOW_ID } from '../../constants';
import * as Analytics from '../../utils/analytics';
import { selectAllAppIds, selectOrg, selectApp } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import userflow from 'userflow.js';
import TrackedButton from '../../helpers/TrackedButton';
import { OrgComponentProps } from '../../typescriptHelpers';
import { createAppRoute } from '../../helpers/routeHelpers';
import { useIsMobile } from '../../mediaQueriesHelpers';

interface DemoModeBannerProps extends OrgComponentProps {
    isDemoInstance?: boolean;
}

const DemoModeBanner: React.FC<DemoModeBannerProps> = (props: DemoModeBannerProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const match = useRouteMatch({ path: routes.org, exact: true });
    const orgIdParam = useSelector((state: RootState) => selectOrg(state)!.orgId);
    const isMobile = useIsMobile();

    const firstAppIdParam = useSelector((state: RootState) => {
        const allAppIds = selectAllAppIds(state);

        if (allAppIds && allAppIds.length > 0) {
            return selectApp(state, allAppIds[0]).appId;
        }

        return null;
    });

    if (match && !props.isDemoInstance) {
        // don't show banner on org settings

        return null;
    }

    const exitDemoMode = () => {
        dispatch(clearAppsDemoMode());

        history.replace(routes.dashboard);
    };

    const addAppClicked = () => {
        // clear demo mode first?
        exitDemoMode();

        dispatch(setWelcomeModalOptions({ shouldShowWelcomeModal: true }));
    };

    const startUserflowDemoTour = () => {
        if (orgIdParam && firstAppIdParam) {
            // navigate home
            const appRoute = createAppRoute(orgIdParam, firstAppIdParam);

            history.push(appRoute);

            userflow.start(USERFLOW_DEMO_FLOW_ID);
        }
    };

    const titleText = isMobile ? '🔮 You are in demo mode 🔮 ' : '🔮 You are viewing these apps in demo mode 🔮';
    const secondaryText =
        props.isDemoInstance ?
            'Data shown is for illustrative purposes only. All actions are disabled (read-only). Want to see more? Request a guided demo or get started for free.'
        :   'Data shown is for illustrative purposes only. All actions are disabled (read-only). Like what you see? Add an app to get started.';

    return (
        <div
            style={{
                backgroundColor: runwayColors.white,
                flexShrink: 0,
                padding: 16,
                borderTopColor: antGrey.grey4,
                borderTopWidth: 5,
                borderTopStyle: 'solid',
                borderBottomColor: antGrey.grey4,
                borderBottomWidth: 5,
                borderBottomStyle: 'solid',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 12,
                }}
            >
                <div style={{ marginRight: 24 }}>
                    <Title style={{ color: antGrey.grey9 }} level={5}>
                        {titleText}
                    </Title>
                    <Text type="secondary">{secondaryText}</Text>
                </div>
                {props.isDemoInstance ?
                    <Space align="center">
                        <TrackedButton
                            eventName={Analytics.Event.ClickLaunchTour}
                            type="primary"
                            onClick={startUserflowDemoTour}
                            orgId={props.orgId}
                        >
                            Launch tour
                        </TrackedButton>
                        <TrackedButton
                            href={MARKETING_SITE_DEMO_URL}
                            eventName={Analytics.Event.ClickDemoModePrimaryCta}
                        >
                            Request guided demo
                        </TrackedButton>
                        {!isMobile && (
                            <TrackedButton
                                type="link"
                                href={APP_SIGNUP_URL}
                                eventName={Analytics.Event.ClickDemoModeSecondaryCta}
                            >
                                Try free
                            </TrackedButton>
                        )}
                    </Space>
                :   <Space>
                        <Button type="primary" icon={<PlusOutlined />} onClick={addAppClicked}>
                            Add app
                        </Button>
                        <Button onClick={exitDemoMode}>Exit demo mode</Button>
                    </Space>
                }
            </div>
        </div>
    );
};

export default DemoModeBanner;
